import type { ComponentProps } from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const popoverContentVariants = cva(
  'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-w-[calc(100vw-2rem)] origin-(--radix-popover-content-transform-origin) rounded-md ring-1 ring-foreground/10 outline-hidden',
  {
    variants: {
      size: {
        auto: 'w-auto',
        default: 'w-72',
        large: 'w-80',
        small: 'w-64',
        trigger: 'w-(--radix-popover-trigger-width) min-w-0',
        wide: 'w-[min(22rem,calc(100vw-2rem))]',
      },
      padding: {
        none: 'p-0',
        compact: 'p-2',
        default: 'p-4',
        spacious: 'p-5',
      },
      elevation: {
        subtle: 'shadow-sm',
        default: 'shadow-md',
        strong: 'shadow-lg',
      },
      overflow: {
        visible: 'overflow-visible',
        hidden: 'overflow-hidden',
      },
    },
    defaultVariants: {
      size: 'default',
      padding: 'default',
      elevation: 'default',
      overflow: 'visible',
    },
  }
)

export type PopoverProps = ComponentProps<typeof PopoverPrimitive.Root>

function Popover({ ...props }: PopoverProps) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />
}

export type PopoverTriggerProps = ComponentProps<typeof PopoverPrimitive.Trigger>

function PopoverTrigger({ ...props }: PopoverTriggerProps) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
}

export type PopoverContentProps = ComponentProps<typeof PopoverPrimitive.Content> &
  VariantProps<typeof popoverContentVariants>

function PopoverContent({
  className,
  align = 'center',
  sideOffset = 4,
  size = 'default',
  padding = 'default',
  elevation = 'default',
  overflow = 'visible',
  ...props
}: PopoverContentProps) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        data-elevation={elevation}
        data-overflow={overflow}
        data-padding={padding}
        data-size={size}
        align={align}
        sideOffset={sideOffset}
        className={cn(popoverContentVariants({ size, padding, elevation, overflow }), className)}
        {...props}
      />
    </PopoverPrimitive.Portal>
  )
}

export type PopoverAnchorProps = ComponentProps<typeof PopoverPrimitive.Anchor>

function PopoverAnchor({ ...props }: PopoverAnchorProps) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor, popoverContentVariants }
